import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { keyBy } from 'lodash'
import useNodesResources from '../shared/resources/api/v1/admin/useNodesResources'
import { useNodeEvents } from '../shared/websocket'
import useThrottledCallback from '../shared/hooks/useThrottledCallback'

const NodesContext = createContext(null)

function BaseNodesProvider({ children }) {
  const { teamSlug } = useParams()
  const { all } = useNodesResources(teamSlug)
  const [nodes, setNodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const loadingRef = useRef(false)

  const loadNodes = useCallback(() => {
    if (loadingRef.current || !teamSlug) {
      return Promise.resolve([])
    }
    loadingRef.current = true
    setError(null)
    return all()
      .then((response) => {
        setNodes(response.nodes)
        return response.nodes
      })
      .catch((err) => {
        setError(err)
        throw err
      })
      .finally(() => {
        setLoading(false)
        loadingRef.current = false
      })
  }, [teamSlug, all])

  useEffect(() => {
    if (!teamSlug) {
      setNodes([])
      setLoading(false)
      return
    }

    setLoading(true)
    loadNodes()
  }, [teamSlug])

  const nodesById = useMemo(() => keyBy(nodes, 'id'), [nodes])

  const value = useMemo(() => ({
    nodes,
    nodesById,
    loading,
    error,
    reload: loadNodes,
  }), [nodes, nodesById, loading, error, loadNodes])

  return (
    <NodesContext.Provider value={value}>
      {children}
    </NodesContext.Provider>
  )
}

function useNodes() {
  const context = useContext(NodesContext)
  if (!context) {
    throw new Error('useNodes must be used within a NodesProvider')
  }
  return context
}

function useNodesSafe() {
  const context = useContext(NodesContext)
  return context || { nodes: [], nodesById: {}, loading: false, error: null, reload: () => Promise.resolve([]) }
}

function NodeEventSubscriber({ children }) {
  const { reload } = useNodes()
  const throttledReload = useThrottledCallback(reload)

  useNodeEvents({
    onCreated: throttledReload,
    onUpdated: throttledReload,
  })

  return children
}

function NodesProvider({ children }) {
  return (
    <BaseNodesProvider>
      <NodeEventSubscriber>
        {children}
      </NodeEventSubscriber>
    </BaseNodesProvider>
  )
}

export { NodesProvider, useNodes, useNodesSafe }
